import { getDeviceType } from "@b2bportal/core-utilities";
import { homesApi } from "@b2bportal/core-utilities";
import {
  ResponseEnum,
  type ShopRequest,
  type ShopResponse,
} from "@b2bportal/homes-api";
import {
  type AvailabilityRequest,
  AvailabilityRequestEnum,
  LodgingCollection,
  LodgingSelectionEnum,
  Platform,
  type LodgingTravelWalletOffer,
} from "@b2bportal/lodging-api";

import { hotelsApi } from "@overrides/utilities";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { HotelsConfig } from "@tenant/hotels";
import { HotelsShopActions, type ShopSearch } from "..";
import {
  getHomeAvailabilityRequest,
  type HotelsState,
} from "../../../../store";

export type FetchHomesShopFulfillResponse = {
  shop: ShopResponse;
  bestOffer?: LodgingTravelWalletOffer;
  search: ShopSearch;
  availabilityTrackingProperties?: Record<string, any>;
};

export const fetchHomesShop = createAsyncThunk<
  FetchHomesShopFulfillResponse,
  { search: ShopSearch; config?: HotelsConfig },
  { state: HotelsState; rejectValue: { error: string } }
>("homesShop/fetchHomesShop", async ({ search, config }, thunkAPI) => {
  const { isDesktopAndUp } = getDeviceType();
  const homesAvailabilityRequest = getHomeAvailabilityRequest(config);

  const availabilityRequest: AvailabilityRequest = {
    progressiveConfig: {},
    stayDates: search.dateRange,
    guests: search.guests,
    platform: isDesktopAndUp ? Platform.Desktop : Platform.Mobile,
    lodgingSelection: {
      lodgingIds: [search.lodgingId],
      preserveOrder: true,
      LodgingSelection: LodgingSelectionEnum.LodgingIds,
    },
    rooms: {
      numberOfRooms: search.rooms ?? 1,
    },
    lodgingCollection: LodgingCollection.NoCollection,
    excludeNonLuxuryLodgings: false,
    AvailabilityRequest: AvailabilityRequestEnum.InitialSearch,
    ...homesAvailabilityRequest,
  };

  try {
    //TODO: use homes availability API
    const availabilityRes =
      await hotelsApi.apiV0HotelsAvailabilityPost(availabilityRequest);

    const lodging =
      availabilityRes.data.lodgings.length > 0
        ? availabilityRes.data.lodgings[0]
        : null;

    if (lodging != null) {
      thunkAPI.dispatch(HotelsShopActions.setLodging(lodging));
    }

    if (lodging?.price == null) {
      thunkAPI.dispatch(HotelsShopActions.setLodgingUnavailable(true));
      return thunkAPI.rejectWithValue({
        error: "Lodging not available",
      });
    }

    const shopRequest: ShopRequest = {
      listingId: {
        id: search.lodgingId,
      },
      stayDetails: {
        dateRange: {
          from: search.dateRange.from,
          until: search.dateRange.until,
        },
        guestDetails: {
          adults: search.guests.adults,
          children: search.guests.children.length,
          infants: 0,
          petsIncluded: Boolean(search.guests.pets),
        },
      },
      distributionChannels:
        homesAvailabilityRequest.homesInventoryOptions?.distributionChannels ??
        [],
    };

    const res = await homesApi.apiV0HomesShopPost(shopRequest);
    const data = res.data;

    switch (data.Response) {
      case ResponseEnum.Success: {
        const bestOffer =
          availabilityRes.data.offers?.offersByProductId[search.lodgingId];
        const availabilityTrackingProperties =
          availabilityRes.data.trackingPropertiesV2;
        return {
          shop: data.value,
          search,
          bestOffer,
          availabilityTrackingProperties,
        };
      }
      case ResponseEnum.Failure:
        return thunkAPI.rejectWithValue({ error: ResponseEnum.Failure });
    }
  } catch (error) {
    // Reject the thunk with an error message
    return thunkAPI.rejectWithValue({ error: String(error) });
  }
});
